import { useState } from 'react';

import { ApiError } from '@/api/client';
import { usePreferences } from '@/app/preferences';
import { usePerson } from '@/auth/roles';
import type { IncidentAction } from '@/domain/types';

export type DecisionAction = Exclude<IncidentAction, 'auto'>;

export interface DecisionValues {
  incident_action: DecisionAction;
  incident_title: string;
  incident_body: string;
}

export function emptyDecision(): DecisionValues {
  return { incident_action: 'none', incident_title: '', incident_body: '' };
}

/**
 * The name recorded on a decision: prefilled from preferences, saved back when the decision is
 * made. Signed in (CHG-050) it is the person the token names, "Name (email)", fixed: nothing to
 * type and nothing to remember. The API records the token's person whatever the body says.
 */
export function useApproverName() {
  const { approver, setApprover } = usePreferences();
  const person = usePerson();
  const [name, setName] = useState(approver);
  if (person) return { name: person.recorded, setName: () => {}, remember: () => {} };
  return { name, setName, remember: () => setApprover(name) };
}

/** Turn an ApiError into inline field errors plus a general message for the dialog. */
export function explainError(error: unknown): {
  fields: Record<string, string>;
  message: string | null;
} {
  if (error instanceof ApiError) {
    if (error.status === 422) {
      const fields = error.violations;
      const detail = error.unprocessable?.detail ?? null;
      return { fields, message: Object.keys(fields).length > 0 ? null : detail };
    }
    return { fields: {}, message: error.message };
  }
  if (error instanceof Error) return { fields: {}, message: error.message };
  return { fields: {}, message: 'Something went wrong.' };
}
